/**
 * Calendar days in a named time zone - pure, no database.
 *
 * The app stores the day a user picks (a due date, a schedule date) as the LOCAL midnight of that
 * day, as stringified UTC ms (`toDbDate(dayjs(date))`). So the calendar day a stored value means is
 * its day in the user's time zone, not its UTC day: for Europe/Stockholm, Oct 7 is stored as
 * 2026-10-06T22:00:00Z. A reader that cuts the ISO string at 10 characters reports Oct 6.
 *
 * The functions here turn a stored instant into the user's calendar day and back, for a given IANA
 * zone (Intl, no dependency): they are how the server, which runs in UTC, says and reads days the
 * way the app does.
 */

const formatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    })
    formatters.set(timeZone, f)
  }
  return f
}

/** Is `timeZone` an IANA zone name this runtime knows (`Europe/Stockholm`)? */
export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== "string" || timeZone.trim() === "") return false
  try {
    formatterFor(timeZone)
    return true
  } catch {
    return false
  }
}

/** The wall-clock fields of `ms` in `timeZone`. */
function wallClock(ms: number, timeZone: string) {
  const out: Record<string, number> = {}
  for (const p of formatterFor(timeZone).formatToParts(new Date(ms))) {
    if (p.type !== "literal") out[p.type] = Number(p.value)
  }
  return { year: out.year!, month: out.month!, day: out.day!, hour: out.hour!, minute: out.minute!, second: out.second! }
}

/** `timeZone`'s offset from UTC at the instant `ms`, in ms (east of UTC is positive). */
export function timeZoneOffsetMs(ms: number, timeZone: string): number {
  const w = wallClock(ms, timeZone)
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - Math.floor(ms / 1000) * 1000
}

const pad = (n: number, width = 2) => String(n).padStart(width, "0")

/** The calendar day of the instant `ms` in `timeZone`, `YYYY-MM-DD`. */
export function localDayOf(ms: number, timeZone: string): string {
  const w = wallClock(ms, timeZone)
  return `${pad(w.year, 4)}-${pad(w.month)}-${pad(w.day)}`
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/

/** Is `value` a date-only string (`YYYY-MM-DD`) naming a real calendar day? */
export function isDayString(value: string): boolean {
  const m = DAY.exec(value)
  if (!m) return false
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const probe = new Date(Date.UTC(y, mo - 1, d))
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d
}

/**
 * The instant (ms) of 00:00 on the calendar day `day` (`YYYY-MM-DD`) in `timeZone` - what the app
 * stores for a picked day. Where that wall-clock time does not exist (a DST change at midnight) it is
 * the first instant of the day. Throws for a string that is not a calendar day.
 */
export function localMidnightMs(day: string, timeZone: string): number {
  if (!isDayString(day)) throw new Error(`not a calendar day: ${day}`)
  const m = DAY.exec(day)!
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  // The offset at the guess, then at the instant that offset gives (a DST change in between moves it)
  let candidate = asUtc - timeZoneOffsetMs(asUtc, timeZone)
  candidate = asUtc - timeZoneOffsetMs(candidate, timeZone)
  // A midnight that does not exist (a DST change at 00:00) lands on the previous evening: step to the day's first instant
  while (localDayOf(candidate, timeZone) < day) candidate += 3600_000
  return candidate
}

// ---------------------------------------------------------------------------
// "Zone or runtime" helpers - the day boundaries the shared operations use.
//
// Each takes an optional `timeZone`. Without one they are exactly what the app has always computed
// with the runtime's own local zone (`new Date(...).setHours(...)`, `getDate()`, `dayjs()`): the SPA
// never passes a zone, so its behaviour is unchanged. The server (a Durable Object whose clock is UTC)
// passes the user's zone (OpsContext.timeZone) and gets the day the app would show.
// ---------------------------------------------------------------------------

/** `day` (`YYYY-MM-DD`) moved by `n` calendar days (negative = back). */
export function addDaysToDay(day: string, n: number): string {
  const m = DAY.exec(day)
  if (!m) throw new Error(`not a calendar day: ${day}`)
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n))
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/** The hour (0-23) of the instant `ms` on the wall clock of `timeZone`, or of the runtime without one. */
export function localHourOf(ms: number, timeZone?: string): number {
  return timeZone ? wallClock(ms, timeZone).hour : new Date(ms).getHours()
}

/** The weekday (0 = Sunday ... 6 = Saturday) of the calendar day the instant `ms` falls on. */
export function localWeekdayOf(ms: number, timeZone?: string): number {
  if (!timeZone) return new Date(ms).getDay()
  const m = DAY.exec(localDayOf(ms, timeZone))!
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay()
}

/** The calendar day of the instant `ms` as `YYYYMMDD` (the recurrence occurrence key shape). */
export function localDayKeyOf(ms: number, timeZone?: string): string {
  if (timeZone) return localDayOf(ms, timeZone).replace(/-/g, "")
  const d = new Date(ms)
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`
}

/** The calendar day of `ms`, `YYYY-MM-DD`, in `timeZone` or, without one, the runtime's own zone. */
export function localDayStringOf(ms: number, timeZone?: string): string {
  if (timeZone) return localDayOf(ms, timeZone)
  const d = new Date(ms)
  return `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** The instant of 00:00 on the calendar day `day` in `timeZone` or, without one, the runtime's own zone. */
export function localMidnightOf(day: string, timeZone?: string): number {
  if (timeZone) return localMidnightMs(day, timeZone)
  const m = DAY.exec(day)
  if (!m) throw new Error(`not a calendar day: ${day}`)
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime()
}

/**
 * The last millisecond of the calendar day `nowMs` falls on, `plusDays` days later (0 = today's end,
 * 1 = tomorrow's end), in `timeZone` or, without one, the runtime's own zone.
 */
export function endOfDayMs(nowMs: number, timeZone?: string, plusDays = 0): number {
  if (!timeZone) {
    const d = new Date(nowMs)
    if (plusDays !== 0) d.setDate(d.getDate() + plusDays)
    d.setHours(23, 59, 59, 999)
    return d.getTime()
  }
  return localMidnightMs(addDaysToDay(localDayOf(nowMs, timeZone), plusDays + 1), timeZone) - 1
}

/**
 * The instant `n` calendar days after `ms` in `timeZone`, keeping the wall-clock time (local midnight
 * stays local midnight across a DST change, which adding 24 h would not). Same as dayjs's
 * `.add(n, "day")` in the runtime's own zone, for a named one.
 */
export function addLocalDays(ms: number, n: number, timeZone: string): number {
  const w = wallClock(ms, timeZone)
  const asUtc = Date.UTC(w.year, w.month - 1, w.day + n, w.hour, w.minute, w.second) + (((ms % 1000) + 1000) % 1000)
  const first = asUtc - timeZoneOffsetMs(asUtc, timeZone)
  return asUtc - timeZoneOffsetMs(first, timeZone)
}
